import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';
import Card from '../TEC/CardTECSession';

type CardMCSummaryHighTrafficUseProps = {
  // Add props if needed
  controller: any,
  runSequence: any,
  MeChartSequence: any,
}


const CardMCSummaryHighTrafficUse = (props: CardMCSummaryHighTrafficUseProps) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);
  const [viewMode, setViewMode] = useState<
    'summary_traffic' | 'traffic_quarter_year' | 'traffic_quarter_3year'
  >('summary_traffic');
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateStr = viewMode === 'summary_traffic'
        ? `${year}-${String(month).padStart(2, '0')}-01 00:00:00`
        : (viewMode === 'traffic_quarter_3year' ? `${endYear - 2}-01-01 00:00:00` : `${endYear}-01-01 00:00:00`);

      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const endDateStr = viewMode === 'summary_traffic'
        ? `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`
        : `${endYear}-12-31 23:59:59`;

      let res = null;

      if (props.MeChartSequence?.sequenceIndex === "Action") {

          let chart = ""
          if (viewMode === "summary_traffic" ) {
            chart = "traffic-24hour"
          } else if (viewMode === "traffic_quarter_year") {
            chart = "traffic-24hour-quarter"
          } else if (viewMode === "traffic_quarter_3year") {
            chart = "traffic-24hour-3-year"
          }

          res = await props.runSequence({
          sequence: "MeChart", action: "QuerySQL",
          chart,
          startDate: startDateStr,
          endDate: endDateStr
        })
      }

      let data = []
      // const response = await fetch(url);
      if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
        data = res?.[0]?.items;
      }
  

      if (Array.isArray(data)) {
        setChartData(data);
      } else {
        setChartData([]);
      }

      const response = await fetch(`http://localhost:3002/api/traffic-24hour?startDate=${startDateStr}&endDate=${endDateStr}&mode=${viewMode}`);
      const sqlData = await response.json();
      console.log('sqlData: ', sqlData);

    
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewMode]);

  const onSearch = () => { fetchData(); };
  const onPrint = () => { window.print(); };
  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(chartData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Traffic24Hour");
    XLSX.writeFile(wb, `Traffic_24Hour_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Helper to generate time labels (e.g., 00:00, 01:00...)
  const timeLabels = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00:00`);

  // Transform SQL Data for Vega
  const getVegaData = () => {
    // chartData from SQL: [{ time_label: '00:00:00', total_count: '5', sort_order: 0 }, ...]
    // We want to ensure all hours are represented, even if count is 0? The SQL does LEFT JOIN so it should be fine.

    // Convert string counts to numbers
    const processedData = chartData.map(d => ({
      ...d,
      count: parseInt(d.count || d.total_count, 10) || 0,
      time: d.time_label, // Use time_label directly
      order: d.sort_order
    }));

    // If viewMode logic is needed, we might need to filter or process differently.
    // For now, assuming the SQL returns the summary for the selected period.

    // For 'summary_traffic', we just show the processed data.
    // For other modes, we might need different queries or post-processing?
    // The current SQL aggregates over the entire period. 
    // To support quarters/years comparison, we'd need modify the SQL or fetch multiple times.
    // Given the task is just "put inside nodejs script", I will stick to the basic integration first.
    // Use 'category' to distiguish lines if we had multiple series.

    return processedData.map(d => ({
      ...d,
      category: d.category || 'Overall'
    }));
  };

  const getSpec = (): VisualizationSpec => {
    const data = getVegaData();
    const isSummary = viewMode === 'summary_traffic';

    if (isSummary) {
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "area", line: true, point: true, tooltip: true },
        encoding: {
          x: { field: "time", type: "ordinal", axis: { title: "Time", labelAngle: -45 }, sort: { field: "order" } },
          y: { field: "count", type: "quantitative", axis: { title: "Visitors" } },
          color: { value: "#4285F4" },
          fillOpacity: { value: 0.2 },
          tooltip: [
            { field: "time", title: "Time" },
            { field: "count", title: "Visitors" }
          ]
        }
      };
    } else {
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "line", point: true, tooltip: true },
        encoding: {
          x: { field: "time", type: "ordinal", axis: { title: "Time", labelAngle: -45 }, sort: { field: "order" } },
          y: { field: "count", type: "quantitative", axis: { title: "Visitors" } },
          color: {
            field: "category",
            type: "nominal",
            scale: viewMode === 'traffic_quarter_year'
              ? { range: ['#4285F4', '#EA4335', '#FBBC05', '#34A853'] }
              : { scheme: 'category10' },
            legend: { title: "Period" }
          },
          tooltip: [
            { field: "time", title: "Time" },
            { field: "category", title: "Period" },
            { field: "count", title: "Visitors" }
          ]
        }
      };
    }
  };

  return (
    <div id="card-mc-summary-high-traffice-use">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        <div style={{ maxWidth: '85%' }}>
          <Button.Group vertical={false} style={{ flexWrap: 'wrap' }}>
            <Button
              active={viewMode === 'summary_traffic'}
              style={viewMode === 'summary_traffic' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('summary_traffic')}
            >
              สรุปช่วงเวลาหนาแน่นการรับบริการ
            </Button>
            <Button
              active={viewMode === 'traffic_quarter_year'}
              style={viewMode === 'traffic_quarter_year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('traffic_quarter_year')}
            >
              ช่วงเวลาหนาแน่นการรับบริการ เทียบแต่ละไตรมาส (รายปี)
            </Button>
            <Button
              active={viewMode === 'traffic_quarter_3year'}
              style={viewMode === 'traffic_quarter_3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setViewMode('traffic_quarter_3year')}
            >
              ช่วงเวลาหนาแน่นการรับบริการ เทียบแต่ละไตรมาส 3 ปีย้อนหลัง
            </Button>
          </Button.Group>
        </div>

        {/* Separate Line Chart Toggle (Always active) */}
        <div>
          <Button.Group>
            <Button
              active={true}
              style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
              icon
            >
              <Icon name='chart line' /> Line
            </Button>
          </Button.Group>
        </div>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={viewMode === 'summary_traffic' ? 'default' : 'yearOnly'}
          year={viewMode === 'summary_traffic' ? year : endYear}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={viewMode === 'summary_traffic' ? setYear : setEndYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={onSearch}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCSummaryHighTrafficUse;
